import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Container from '@/components/ui/Container'
import Lines from '@/components/ui/Lines'
import Logo from '@/components/ui/Logo'
import { SITE } from '@/content/site'
import { PARTNER_MEDIA_URLS } from '@/utils/siteMeta'
import { resolveNav, type NavLocale } from '@/cms/nav'

/** Map an i18next language onto the locales the CMS stores labels for. */
function toNavLocale(language: string): NavLocale {
  if (language.startsWith('zh')) {
    return /Hans|zh-CN|zh-SG/i.test(language) ? 'zh-Hans' : 'zh-Hant'
  }
  return 'en'
}

export default function Footer() {
  const { t, i18n } = useTranslation()

  // Quick links are derived from the same CMS nav tree the header uses, so the
  // two can't drift. They had already drifted: this list was hand-written and
  // missing /magazine and /articles, and its heading read "首頁" over a list
  // that had no home link in it. External items are skipped — a footer's quick
  // links are internal navigation.
  const quickLinks = resolveNav(toNavLocale(i18n.language), t).filter((i) => !i.isExternal)

  return (
    <footer className="bg-ink text-surface mt-section-y">
      <Container className="py-section-y">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 md:gap-10">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" aria-label={t('nav.home')}>
              {/* Phase 1 mobile (2026-10-13): was a fixed `h-20` (80px) on
                  all viewports. Footer logo is now driven by the shared
                  <Logo size="md"> (48 → 56 px) — keeps the dark-on-cream
                  wrapper that makes the logo legible on the dark footer. */}
              <span className="inline-block bg-surface rounded-yada p-2">
                <Logo variant="horizontal" size="md" eager />
              </span>
            </Link>
            <p className="mt-4 text-sm text-surface/80 max-w-md leading-relaxed">
              {t('common.tagline')}
            </p>
          </div>

          {/* Quick links — derived from the CMS nav, same source as the header */}
          <div>
            <h3 className="text-base font-semibold text-accent mb-3 md:mb-4">
              {t('nav.quickLinks')}
            </h3>
            <ul className="grid grid-cols-2 sm:grid-cols-1 gap-x-3 gap-y-2 text-sm">
              {quickLinks.map((item) => (
                <li key={item.key}>
                  <Link to={item.href} className="text-surface/80 hover:text-accent transition-colors py-1 inline-block">
                    <Lines text={item.label} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-base font-semibold text-accent mb-3 md:mb-4">
              <Lines text={t('common.getInTouch')} />
            </h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={SITE.contact.whatsapp.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-surface/80 hover:text-accent transition-colors"
                >
                  <Icon icon="lucide:message-circle" className="text-base flex-shrink-0" />
                  <span className="break-all">{SITE.contact.whatsapp.display}</span>
                </a>
              </li>
              <li>
                <a
                  href={SITE.contact.email.link}
                  className="flex items-center gap-2 text-surface/80 hover:text-accent transition-colors"
                >
                  <Icon icon="lucide:mail" className="text-base flex-shrink-0" />
                  <span className="break-all">{SITE.contact.email.display}</span>
                </a>
              </li>
              <li>
                <a
                  href={SITE.contact.website.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-surface/80 hover:text-accent transition-colors"
                >
                  <Icon icon="lucide:globe" className="text-base flex-shrink-0" />
                  <span>{SITE.contact.website.display}</span>
                </a>
              </li>
              <li>
                <a
                  href={SITE.social.facebook.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-surface/80 hover:text-accent transition-colors"
                >
                  <Icon icon="lucide:facebook" className="text-base flex-shrink-0" />
                  <span>{SITE.social.facebook.display}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Friendly links — editorial cross-link. Anchor text uses the brand
              name + a one-line description so it's editorial (not a generic
              "click here" link) and read by Google as a contextual reference.
              See `common.partners` in i18n for the description copy. */}
          <div>
            <h3 className="text-base font-semibold text-accent mb-3 md:mb-4">
              {t('common.partners.title')}
            </h3>
            <p className="text-xs text-surface/60 leading-relaxed mb-3">
              {t('common.partners.blurb')}
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={PARTNER_MEDIA_URLS.goodschool}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <span className="block font-semibold text-accent group-hover:text-accent-soft transition-colors">
                    {t('common.partners.goodschool.name')}
                  </span>
                  <span className="block text-xs text-surface/70 leading-snug mt-1">
                    {t('common.partners.goodschool.description')}
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-5 border-t border-surface/20 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-[10px] leading-snug tracking-tight whitespace-nowrap text-surface/60 text-center sm:text-left sm:text-[11px] sm:whitespace-normal">
            © {new Date().getFullYear()} {SITE.brand.legalFull} · {t('common.footer.rights')}
          </p>
          <p className="text-xs leading-snug tracking-tight text-surface/60 text-center sm:text-right sm:text-[10px]">
            {SITE.brand.legalEnglish}
          </p>
        </div>
      </Container>
    </footer>
  )
}
